import styles from "./WelcomeToSweden.module.scss";
import { Helmet } from "react-helmet-async";
import { useEffect, useRef } from "react";
import Modal, { DialogProps, ModalHandle } from "../../components/Modal/Modal";

const WelcomeToSweden = () => {
  const modalRef = useRef<ModalHandle | null>(null);
  const pageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Next-section buttons
    const nextButtons = Array.from(document.querySelectorAll(".next-section"));
    const sectionOrder = ["intro", "hello", "stories", "irec", "event-details"];

    const onNextClick = (e: Event) => {
      e.preventDefault();
      const btn = e.currentTarget as HTMLElement;
      const currentSection = btn.closest("section");
      if (!currentSection) return;
      const currentId = currentSection.id;
      const currentIndex = sectionOrder.indexOf(currentId);
      const nextIndex = currentIndex + 1;
      if (nextIndex < sectionOrder.length) {
        const nextSection = document.getElementById(sectionOrder[nextIndex]);
        if (nextSection) {
          nextSection.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    };

    nextButtons.forEach((b) => b.addEventListener("click", onNextClick));

    // Story modal handlers (use Modal component via ref)
    const story01 = document.getElementById("story01-btn");
    const story02 = document.getElementById("story02-btn");
    const story03 = document.getElementById("story03-btn");

    const openStory = (props: DialogProps) => {
      modalRef.current?.onOpen(props);
    };

    const handleStory01 = () => {
      openStory({
        title: "Helen Saragih",
        description:
          "I came to this church in my very first week in Stockholm. Soon I realized that the pressure of my Master&apos;s and the reality of living abroad could be overwhelming, and it was easy to feel lost while finding your &apos;place&apos; in a new land.<br/><br/>But staying close to the church and having a community keep me grounded and rooted in faith. I&apos;ve also made meaningful friendships in the community that I&apos;ve grown together with. Four years later, even though I now live an hour away in Västerås, the church still feels like home.",
      });
    };

    const handleStory02 = () => {
      openStory({
        title: "Sara Rivera Méndez",
        description:
          "I was in Stockholm due to an internship the last year, and even being more than nine thousands kilometers far away from my homeland (I'm from Mexico), I've finally been able to reconnect with my spirituality and with something so beautiful like having a community open to know you and help you. The time that I spent in the church is one that I will never forget. The people who make me go back again and again every Sunday, are truly one of a kind. And the lessons I learned, are really meaningful.",
      });
    };

    const handleStory03 = () => {
      openStory({
        title: "Bobby Herman",
        description:
          'I came to Sweden as a master&apos;s student at KI. I brought my family (my wife and two daughters) along with me. We came from Indonesia barely knew anyone except our seniors from our undergrad campus who have resided in Sweden. By God&apos;s grace, we were introduced with friends from IREC Stockholm. Not only "adult-age" friends but also "children-age" friends for our daughters. Our time in Sweden has been a blessed moment from God to keep our family anchored in His word and promises even though we were far from home through such a beautiful fellowship in IREC Stockholm. If this is your first time coming to Sweden, whether you come alone or with your loved ones, I truly encourage you to find a Christ-centered community like IREC Stockholm.',
      });
    };

    story01?.addEventListener("click", handleStory01);
    story02?.addEventListener("click", handleStory02);
    story03?.addEventListener("click", handleStory03);

    let isAnimating = false;
    let animationFrameId: number | undefined;
    let wheelTimeout: number | undefined;
    const mobileBreakpoint = 768; // px

    const getSections = () =>
      Array.from(
        pageRef.current?.querySelectorAll<HTMLElement>("section") ?? [],
      );

    const getSectionTop = (section: HTMLElement) => {
      const container = pageRef.current;
      if (!container) return 0;
      return section.offsetTop - 24;
    };

    const smoothScrollToSection = (targetSection: HTMLElement) => {
      const container = pageRef.current;
      if (!container || !targetSection) return;

      const startPosition = container.scrollTop;
      const targetPosition = Math.max(getSectionTop(targetSection), 0);
      const distance = targetPosition - startPosition;
      const duration = 1400;
      const startTime = performance.now();

      const easing = (t: number) => {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      };

      const step = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easing(progress);
        container.scrollTop = startPosition + distance * eased;

        if (progress < 1) {
          animationFrameId = window.requestAnimationFrame(step);
        } else {
          isAnimating = false;
        }
      };

      isAnimating = true;
      if (animationFrameId !== undefined) {
        window.cancelAnimationFrame(animationFrameId);
      }
      animationFrameId = window.requestAnimationFrame(step);
    };

    const handleWheel = (e: WheelEvent) => {
      const isMobile = window.innerWidth < mobileBreakpoint;

      if (isMobile) {
        return;
      }

      if (isAnimating) {
        e.preventDefault();
        return;
      }

      const delta = e.deltaY;
      if (Math.abs(delta) < 10) return;

      e.preventDefault();
      const sections = getSections();
      if (!sections.length) return;

      const currentIndex = sections.findIndex((section) => {
        const top = section.offsetTop - 24;
        const bottom = top + section.offsetHeight;
        return (
          top <= (pageRef.current?.scrollTop ?? 0) + 120 &&
          bottom >= (pageRef.current?.scrollTop ?? 0) + 120
        );
      });

      const safeIndex = currentIndex >= 0 ? currentIndex : 0;
      const nextIndex =
        delta > 0
          ? Math.min(safeIndex + 1, sections.length - 1)
          : Math.max(safeIndex - 1, 0);

      if (nextIndex !== safeIndex) {
        const targetSection = sections[nextIndex];
        if (targetSection) {
          smoothScrollToSection(targetSection);
        }
      }
    };

    const handleScroll = () => {
      const isMobile = window.innerWidth < mobileBreakpoint;

      if (isMobile) {
        return;
      }

      if (isAnimating) return;
      window.clearTimeout(wheelTimeout);
      wheelTimeout = window.setTimeout(() => {
        const sections = getSections();
        if (!sections.length) return;
        const container = pageRef.current;
        if (!container) return;

        const currentIndex = sections.findIndex((section) => {
          const top = section.offsetTop - 24;
          const bottom = top + section.offsetHeight;
          return (
            top <= container.scrollTop + 120 &&
            bottom >= container.scrollTop + 120
          );
        });

        const safeIndex = currentIndex >= 0 ? currentIndex : 0;
        const targetSection = sections[safeIndex];
        if (targetSection) {
          const distanceToTarget = Math.abs(
            container.scrollTop - getSectionTop(targetSection),
          );
          if (distanceToTarget > 60) {
            smoothScrollToSection(targetSection);
          }
        }
      }, 140);
    };

    pageRef.current?.addEventListener("wheel", handleWheel, { passive: false });
    pageRef.current?.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      nextButtons.forEach((b) => b.removeEventListener("click", onNextClick));
      story01?.removeEventListener("click", handleStory01);
      story03?.removeEventListener("click", handleStory03);
      if (animationFrameId !== undefined) {
        window.cancelAnimationFrame(animationFrameId);
      }
      window.clearTimeout(wheelTimeout);
      pageRef.current?.removeEventListener(
        "wheel",
        handleWheel as EventListener,
      );
      pageRef.current?.removeEventListener(
        "scroll",
        handleScroll as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    const setBgVars = () => {
      const isMobile =
        typeof window !== "undefined" && window.innerWidth <= 960;
      const container = pageRef.current;
      if (!container) return;
      container.style.setProperty(
        "--irec-bg",
        "url('https://www.nordiskamuseet.se/wp-content/uploads/2025/08/Nordiska-museet-djurgarden-host.jpg')",
      );
      container.style.setProperty(
        "--intro-bg",
        isMobile
          ? "url('/welcome-to-sweden-bg-portrait.png')"
          : "url('/welcome-to-sweden-bg.png')",
      );
    };

    setBgVars();
    window.addEventListener("resize", setBgVars);
    return () => window.removeEventListener("resize", setBgVars);
  }, []);

  return (
    <>
      <Helmet>
        <title>
          Welcome to Sweden, 5 September 2026 | International Reformed
          Evangelical Church in Stockholm
        </title>
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta
          name="description"
          content="We are Bible-believing, Reformed church that aims to share the Gospel to the people in Stockholm. Join us on Sundays for worship in English. Welcome to International Reformed Evangelical Church Stockholm (PRII Stockholm)."
        />
        <meta
          name="keywords"
          content="international church stockholm, reformed church stockholm, evangelical church stockholm, IREC stockholm, PRII stockholm, protestant church, bible study stockholm, international church, church in stockholm, church in sweden, christian church stockholm, christian church sweden, sunday service stockholm, sunday service sweden"
        />
        <meta
          itemProp="description"
          content="We are Bible-believing, Reformed church that aims to share the Gospel to the people in Stockholm. Join us on Sundays for worship in English. Welcome to International Reformed Evangelical Church Stockholm (PRII Stockholm)."
        />
        <meta
          name="og:title"
          property="og:title"
          content="Welcome to Sweden, 5 September 2026 | International Reformed Evangelical Church in Stockholm"
        />
        <meta
          name="og:description"
          property="og:description"
          content="We are Bible-believing, Reformed church that aims to share the Gospel to the people in Stockholm. Join us on Sundays for worship in English. Welcome to International Reformed Evangelical Church Stockholm (PRII Stockholm)."
        />
        <meta name="og:type" property="og:type" content="website" />
        <meta
          name="og:url"
          property="og:url"
          content="https://irec-stockholm.org/welcome-to-sweden"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Welcome to Sweden, 5 September 2026 | International Reformed Evangelical Church in Stockholm"
        />
        <meta
          name="twitter:description"
          content="We are Bible-believing, Reformed church that aims to share the Gospel to the people in Stockholm. Join us on Sundays for worship in English."
        />
        <meta
          name="og:image"
          property="og:image"
          content="https://irec-stockholm.org/mother_church.png"
        />
        <meta
          name="og:image:alt"
          property="og:image:alt"
          content="Mother Church - IREC Stockholm's main church building for Sunday worship and fellowship"
        />
        <meta name="og:image:width" property="og:image:width" content="1200" />
        <meta name="og:image:height" property="og:image:height" content="630" />
        <meta
          name="twitter:image"
          content="https://irec-stockholm.org/mother_church.png"
        />
        <link
          rel="canonical"
          href="https://irec-stockholm.org/welcome-to-sweden"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Carter+One&family=Changa:wght@200..800&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <div className={styles.welcomePage} ref={pageRef}>
        <section id="intro" className={styles.intro}>
          <a href="#" className={styles.introLogo}>
            <img src="/welcome-to-sweden-logo.svg" alt="IREC Stockholm Logo" />
          </a>
          <div className={styles.introWrapper}>
            <div className={`${styles.title} ${styles.introTitle}`}>
              <h6>5 Sep 2026 / 10:30 AM</h6>
              <h1>High Agency</h1>
              <h2>in a new land</h2>
            </div>
            <div className={styles.introDesc}>
              <button className={`${styles.introButton} next-section`}>
                Start
              </button>
            </div>
          </div>
          <div className={styles.introOrganizer}>
            Organized by{" "}
            <a href="https://irec-stockholm.org/" target="_blank">
              IREC Stockholm
            </a>
          </div>
        </section>
        <section id="hello" className={styles.hello}>
          <div className={styles.helloTitle}>
            <h3>Are you new in Sweden?</h3>
            <h4>Or even been here for quite some time.</h4>
          </div>
          <div className={styles.helloDesc}>
            <p>
              If you're new to Sweden, how has the journey been so far? Has it
              been exciting? Did you encounter unexpected surprises, or has
              adjusting felt a little overwhelming? And if you've been here for
              a while, how have things turned out? Do you now find yourself
              confidently ordering everything in Swedish like a local?
            </p>
            <p>
              Whether you're here to study, work, or begin a new season of life,
              moving to a new country is a major transition. Looking back on
              your first days in Sweden, you probably remember the challenges
              you have conquered. Like Odysseus striving to navigate his voyage
              home, you're now navigating your own journey, only this time, it's
              one that is away from the community you've grown to.
            </p>
            <p>
              Moving to a new land can reveal the basic human desire to stay in
              control. We create spreadsheets, set goals, and do our best to
              shape our future. Yet life often reminds us that not everything is
              within our control. The good news is that God invites us into
              something greater than self-reliance. Through the church and its
              community, we learn to trust His guidance and grow in His
              purposes. As you begin this new season, we hope you'll find a
              place to quiet the noise, walk with God, and grow alongside His
              people.
            </p>
            <button
              className={`${styles.ghost} ${styles["next-section"]} next-section`}
            >
              How others did it
            </button>
          </div>
        </section>
        <section id="stories" className={styles.stories}>
          <div className={styles.storiesWrapper}>
            <div className={styles.storiesTitle}>
              <h4>Here are stories from</h4>
              <h5>our friends</h5>
              <p>
                We&apos;ve asked some of our friends to share their stories.
                Some are still residing in Sweden, some spent some time with us
                and enjoyed the fellowship together. We hope their experience
                inspires you to join us in this event.
              </p>
            </div>
            <div className={styles.storiesContact}>
              <h6>Wonder for whom this event is for?</h6>
              <button className={`${styles["next-section"]} next-section`}>
                About the event
              </button>
            </div>
          </div>
          <div className={styles.storiesList}>
            <div className={styles.storiesCardContainer}>
              <div className={styles.storiesCard}>
                <div className={styles.storiesDesc}>
                  <h6>Helen Saragih</h6>
                  <div className={styles.subtitle}>
                    Ph.D. student at MDU
                    <br />
                    Since 2021
                  </div>
                </div>
                <div
                  className={`${styles.btn} ${styles.active}`}
                  id="story01-btn"
                >
                  <span
                    className={`${styles.icon} ${styles.next} ${styles.storiesIconBack}`}
                  ></span>
                </div>
              </div>
              <div className={styles.storiesCard}>
                <div className={styles.storiesDesc}>
                  <h6>Sara Rivera Méndez</h6>
                  <div className={styles.subtitle}>
                    Internship
                    <br />
                    2025
                  </div>
                </div>
                <div
                  className={`${styles.btn} ${styles.active}`}
                  id="story02-btn"
                >
                  <span
                    className={`${styles.icon} ${styles.next} ${styles.storiesIconBack}`}
                  ></span>
                </div>
              </div>
              <div className={styles.storiesCard}>
                <div className={styles.storiesDesc}>
                  <h6>Bobby Herman</h6>
                  <div className={styles.subtitle}>
                    Master&apos;s student at KI
                    <br />
                    2021 - 2023
                  </div>
                </div>
                <div
                  className={`${styles.btn} ${styles.active}`}
                  id="story03-btn"
                >
                  <span
                    className={`${styles.icon} ${styles.next} ${styles.storiesIconBack}`}
                  ></span>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section id="irec" className={styles.irec}>
          <div className={styles.irecWrapper}>
            <div className={styles.irecTitle}>
              <h3>
                Welcome to Sweden is a yearly event organized by IREC Stockholm
                for those who are new in town.
              </h3>
            </div>
            <div className={styles.irecDesc}>
              <p>
                If you are looking for a community to grow, we would like to
                welcome you to our church and community. Whether you will be
                here for 2 years, less, indefinitely, or have been here for some
                time, you are very welcome to join.
              </p>
              <button
                className={`${styles.ghost} ${styles["next-section"]} next-section`}
              >
                Event details
              </button>
            </div>
          </div>
        </section>
        <section id="event-details" className={styles.eventDetails}>
          <div className={styles.wrapper}>
            <div className={styles.title}>
              <h1>High agency</h1>
              <h2>in a new land?</h2>
              <h6>Sermon by Rev. Billy Kristanto</h6>
            </div>
            <div className={styles.details}>
              <p>Date and time</p>
              <h6>Saturday, Sep 5 at 10:30 AM</h6>
              <p>Location</p>
              <h6>Storgatan 49, 171 52 Solna</h6>
              <p>Any questions?</p>
              <h6>
                <a
                  href="https://www.instagram.com/irec.stockholm/"
                  target="_blank"
                >
                  Instagram
                </a>{" "}
                or <a href="mailto:irec.stockholm@gmail.com">email</a>
              </h6>
            </div>
          </div>
          <div className={styles.desc}>
            <h6>The program</h6>
            <p>
              We will start with the main event, giving you a taste of how the
              fellowship in our community is. We’ll also hear a sermon by Rev.
              Billy Kristanto on the theme “High Agency in a New Land?” to
              encourage you as you begin this new chapter. Afterwards we’ll
              enjoy a table fellowship together with lunch and of course,
              coffee.
            </p>
            <p>
              To get to know each other even better, we are arranging an outing
              to Nordiska Museet. Families with children can choose between
              visiting the museum or spending time at the kid-friendly outdoor
              playground in Annedalsparken, Solna. The outing is optional and
              free of charge. If you would like to join, please make an RSVP in
              advance.
            </p>
            <a
              style={{ marginTop: "8px", marginBottom: "8px" }}
              className={styles.btn}
              href="https://forms.gle/XyXgr8L39fVLSx25A"
            >
              RSVP now
            </a>
            <p>Latest by Tuesday, Sep 1 (23:59)</p>
          </div>
        </section>
      </div>
      <Modal ref={modalRef} />
    </>
  );
};

export default WelcomeToSweden;
